import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { snagService } from '../../../services/snagService';
import { useLanguage } from '../../../contexts/LanguageContext';

const SnagActivityTimeline = ({ snag, comments, onCommentAdded, companyId }) => {
  const { t } = useLanguage();
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleAddComment = async () => {
    if (!newComment?.trim()) return;
    setSubmitting(true);
    const result = await snagService?.addComment(snag?.id, companyId, newComment?.trim());
    if (!result?.error) {
      setNewComment('');
      onCommentAdded?.(result?.data);
    }
    setSubmitting(false);
  };

  // Build timeline events: creation + status changes from snag data + comments
  const timelineEvents = [
    {
      id: 'created',
      type: 'created',
      icon: 'Plus',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      text: `${t('snagRaisedBy')} ${snag?.user_profiles?.full_name || t('unknown')}`,
      timestamp: snag?.created_at
    },
    ...(snag?.status === 'resolved' && snag?.resolved_at ? [{
      id: 'resolved',
      type: 'resolved',
      icon: 'CheckCircle2',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      text: `${t('resolvedBy')} ${snag?.resolver?.full_name || t('unknown')}`,
      timestamp: snag?.resolved_at
    }] : []),
    ...comments?.map(c => ({
      id: c?.id,
      type: 'comment',
      icon: 'MessageSquare',
      iconBg: 'bg-muted',
      iconColor: 'text-muted-foreground',
      text: c?.comment,
      author: c?.user_profiles?.full_name || t('unknown'),
      timestamp: c?.created_at
    }))
  ]?.sort((a, b) => new Date(a?.timestamp) - new Date(b?.timestamp));

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
        <Icon name="Activity" size={18} />
        {t('activityTimeline')}
      </h3>

      <div className="space-y-4 mb-6">
        {timelineEvents?.map((event, idx) => (
          <div key={event?.id} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${event?.iconBg}`}>
                <Icon name={event?.icon} size={14} className={event?.iconColor} />
              </div>
              {idx < timelineEvents?.length - 1 && (
                <div className="w-px flex-1 bg-border mt-1" />
              )}
            </div>
            <div className="flex-1 pb-4">
              {event?.author && (
                <p className="text-xs font-medium text-foreground mb-0.5">{event?.author}</p>
              )}
              <p className="text-sm text-foreground">{event?.text}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {new Date(event?.timestamp)?.toLocaleString('en-GB', {
                  day: 'numeric', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit'
                })}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Add comment */}
      <div className="border-t border-border pt-4">
        <label className="block text-sm font-medium text-foreground mb-2">{t('addComment')}</label>
        <textarea
          value={newComment}
          onChange={e => setNewComment(e?.target?.value)}
          placeholder={t('addCommentPlaceholder')}
          rows={3}
          className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent resize-none mb-3"
        />
        <Button
          variant="outline"
          size="sm"
          iconName={submitting ? 'Loader2' : 'Send'}
          onClick={handleAddComment}
          disabled={submitting || !newComment?.trim()}
        >
          {submitting ? t('posting') : t('postComment')}
        </Button>
      </div>
    </div>
  );
};

export default SnagActivityTimeline;
